import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { studentsApi } from '../services/api';
import { Award, Download, Calendar, CheckCircle } from 'lucide-react';

interface Certificate {
  id: string;
  courseId: string;
  courseTitle: string;
  completedAt: string;
  certificateUrl?: string;
}

const Certificates: React.FC = () => {
  const { } = useAuth();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await studentsApi.getCertificates();
      setCertificates(response.data.data?.certificates || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
      setCertificates([]); // Mostrar vacío si hay error
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (courseId: string) => {
    try {
      const response = await studentsApi.downloadCertificate(courseId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado-${courseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading certificate:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Certificados</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Tus logros y certificaciones obtenidas</p>
        </div>
      </div>

      {certificates.length === 0 ? (
        <div className="text-center py-12 cyber-container">
          <Award size={64} className="mx-auto text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-400 mb-2">
            Aún no tienes certificados
          </h3>
          <p className="text-gray-600 dark:text-gray-500 mb-6">
            Completa tus cursos para obtener certificados oficiales
          </p>
          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-400/20 dark:to-orange-500/20 border border-yellow-300 dark:border-yellow-400/30 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
              <CheckCircle size={16} />
              <span className="font-medium">¿Sabías que?</span>
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-sm mt-1">
              Los certificados se generan automáticamente al completar el 100% de un curso
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.map((certificate) => (
            <div key={certificate.id} className="cyber-container group hover-lift">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto">
                  <Award size={32} className="text-white" />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    {certificate.courseTitle}
                  </h3>
                  
                  <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 text-sm mb-4">
                    <Calendar size={14} />
                    <span>
                      Completado el {new Date(certificate.completedAt).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleDownload(certificate.courseId)}
                  className="w-full cyber-button-primary flex items-center justify-center gap-2"
                >
                  <Download size={16} />
                  Descargar PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cyber-container">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Información sobre Certificados</h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>• Los certificados se emiten automáticamente al completar el 100% de un curso</p>
          <p>• Todos los certificados incluyen código de verificación único</p>
          <p>• Los certificados son válidos y reconocidos por la industria</p>
          <p>• Puedes compartir tus certificados en redes profesionales como LinkedIn</p>
        </div>
      </div>
    </div>
  );
};

export default Certificates;